import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Col } from 'react-bootstrap';
import Modal from 'react-bootstrap/Modal';
import usePageTitle from 'src/utils/SetPageTitle';
import { useLocation, useNavigate } from 'react-router-dom';
import Breadcrumb from 'src/components/Breadcrumb';
import { useSetBreadcrumbData } from 'src/utils/SetBreadcrumbData';
import VehicleTrackingDetails from 'src/components/CreateDuplicateVehicleTracking/VehicleTrackingDetails';
import UpdatePlanning from 'src/components/CreateDuplicateVehicleTracking/UpdatePlanning';
import SelectWorkflow from 'src/components/CreateDuplicateVehicleTracking/SelectWorkflow';
import ReviewAndConfirm from 'src/components/CreateDuplicateVehicleTracking/ReviewAndConfirm';
import VehicleTrackingDuplicacyModal from 'src/components/VehicleTrackingCreate/Outward/VehicleTrackingDuplicacyModal';
import 'src/style/createDuplicateVehicleTracking.css';
import {
  resetVehicleTrackingDetails,
  getVehicleTrackingDetail,
} from 'src/redux/Actions/VehicleTracking';
import {
  createDuplicateVehicleTracking,
  validateAndGetDuplicateObjectsForVehicleTracking,
} from 'src/redux/Actions/VehicleTrackingOutward';
import { useDispatch, useSelector } from 'react-redux';

const updateLoadSequence = (loads) =>
  (loads || []).map((loadObj, index) => {
    const loadNumber = index + 1;
    return {
      ...loadObj,
      load_number: loadNumber,
      title: `Loading ${String(loadNumber).padStart(2, '0')}`,
    };
  });

const getNumberOrNull = (value) => {
  if (value === undefined || value === null || value === '') return null;
  const numericValue = Number(value);
  return Number.isNaN(numericValue) ? value : numericValue;
};

const buildLoadHeadersPayload = (loadLineItemList) =>
  (loadLineItemList || [])
    .map((loadObj) => {
      const lineItems = loadObj.deliveries.flatMap((deliveryObj) =>
        deliveryObj.line_items.map((lineItem) => ({
          delivery_line_item_id: lineItem.delivery_line_item_id,
          pack_type_id: deliveryObj.pack_type?.id ?? null,
          pack_quantity: getNumberOrNull(deliveryObj.pack_quantity),
          receiving_quantity: getNumberOrNull(lineItem.quantity),
        })),
      );

      return { line_items: lineItems };
    })
    .filter((loadHeader) => loadHeader.line_items.length);

const duplicateVehicleTrackingSteps = [
  {
    id: 1,
    title: 'Vehicle Tracking Details',
    description: 'Choose the correct logistical path for this vehicle record.',
  },
  {
    id: 2,
    title: 'Add/Edit Delivery & Load',
    description: 'Choose the correct delivery & load for this vehicle record.',
  },
  {
    id: 3,
    title: 'Step 03: Select Workflow & Station',
    description:
      'Select the workflow and station for the duplicate vehicle tracking process.',
  },
  {
    id: 4,
    title: 'Step 04: Final Confirmation',
    description:
      'Review all details and confirm to create the duplicate vehicle tracking.',
  },
];

export default function CreateDuplicateVehicleTracking() {
  usePageTitle('Vehicle Tracking - Duplicate Vehicle Tracking');
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [selectedStep, setSelectedStep] = useState(1);
  const authReducer = useSelector((state) => state.AuthReducer);
  const organisationId = authReducer.userData.organisations?.id;
  const themeColor = authReducer?.userData?.tenant?.theme;

  useSetBreadcrumbData(location.pathname, 'Duplicate Tracking');
  const refVehicleTrackingData = location.state?.refVehicleTrackingData;
  useSetBreadcrumbData(location.pathname, 'Duplicate Vehicle Tracking');
  const vehicleTrackingDataReducer = useSelector(
    (state) => state.VehicleTrackingReducer,
  );

  const vehicleTrackingOutwardReducer = useSelector(
    (state) => state.VehicleTrackingOutwardReducer,
  );

  const { vehicleTrackingDetails } = vehicleTrackingDataReducer;

  // Prevents prefill from running more than once
  const prefillFormDataFromRef = useRef(false);
  const submitLockRef = useRef(false);
  const [isDuplicacyModalOpen, setIsDuplicacyModalOpen] = useState(false);

  // Basicdetail form
  const [vehicleTrackigDetailFormData, setVehicleTrackigDetailFormData] =
    useState({
      transporterId: null,
      transporterName: null,
      selectedVehicleOption: null,
      vehicleNumber: null,
      workflowId: null,
      plant: null,
      driverName: null,
      driverMobileNumber: null,
      driverLicenseNumber: null,
      licenseValidity: null,
      remarks: null,
      pucNumber: null,
      pucStartDate: null,
      pucEndDate: null,
      registrationCertificate: null,
      insuranceNumber: null,
      insuranceStartDate: null,
      insuranceEndDate: null,
      fitnessCertificateNumber: null,
      fitnessCertificateStartDate: null,
      fitnessCertificateEndDate: null,
      capacity: null,
      unitOfMeasurementId: null,
      tankCondition: null,
      informationPanel: null,
      lidCondition: null,
      tyreCondition: null,
      tremCard: null,
      reflectors: null,
      safetyHelmet: null,
      safetyShoes: null,
      safetyHandgloves: null,
      safetyGoggles: null,
    });
  const [mobileNumberError, setMobileNumberError] = useState(false);
  // Workflow form
  const [selectedWorkflowObj, setSelectedWorkflowObj] = useState(null);
  const [selectedStageObj, setSelectedStageObj] = useState(null);
  // Delivery & load form

  const [deliveryLineItem, setDeliveryLineItem] = useState([]);
  const [loadLineItem, setLoadLineItem] = useState([]);
  const [disableSubmitButton, setDisableSubmitButton] = useState(false);

  function handleVehicleTrackingDetailChange(e) {
    const { name, type, checked, transporterName, label, isCreateOption } =
      e.target;
    let { value } = e.target;

    if (name === 'driverMobileNumber') {
      value = value.replace(/\D/g, '').slice(0, 10);
      setMobileNumberError(value.length > 0 && value.length !== 10);
    }

    if (name === 'driverLicenseNumber') {
      value = value.replace(/[^a-zA-Z0-9]/g, '');
    }
    if (name === 'workflowId') {
      setSelectedWorkflowObj((prev) => {
        if (!value) return null;
        if (prev?.id === value) return prev;
        return { id: value, name: label ?? null };
      });
      setSelectedStageObj(null);
    }

    setVehicleTrackigDetailFormData((prev) => {
      if (
        name === 'transporterId' &&
        prev.transporterId === value &&
        prev.transporterName === transporterName
      ) {
        return prev;
      }

      const updatedData = {
        ...prev,
        [name]: type === 'checkbox' ? checked : value,
      };

      if (name === 'transporterId') {
        updatedData.transporterName = transporterName;
      }

      if (name === 'transporterId' && !isCreateOption) {
        updatedData.selectedVehicleOption = null;
        updatedData.vehicleNumber = null;
      }

      return updatedData;
    });
  }

  function handleStepClick(stepId) {
    const isGoingBackOrCurrent = stepId <= selectedStep;
    const isNextStepOnly = stepId === selectedStep + 1;

    if (isGoingBackOrCurrent || isNextStepOnly) {
      setSelectedStep(stepId);
    }
  }

  function getTransporterAndVehiclePayload() {
    const payload = {};

    if (vehicleTrackingDetails?.transporter) {
      payload.transporter_id = vehicleTrackigDetailFormData?.transporterId;
    } else {
      payload.transporter_extra_data =
        vehicleTrackigDetailFormData?.transporterName;
    }

    if (vehicleTrackingDetails?.vehicle) {
      payload.vehicle_id =
        vehicleTrackigDetailFormData?.selectedVehicleOption?.value;
    } else {
      payload.vehicle_number = vehicleTrackigDetailFormData?.vehicleNumber;
    }

    return payload;
  }

  function buildDuplicateTrackingPayload() {
    const isDeliveryAttached = deliveryLineItem.length > 0;
    const loadHeaders = buildLoadHeadersPayload(loadLineItem);
    const deliveryIds = deliveryLineItem.map(
      (deliveryObj) => deliveryObj.delivery_id,
    );

    return {
      tenant_organisation_id: organisationId,
      workflow_id: selectedWorkflowObj?.id,
      workflow_stage_id: selectedStageObj?.id,
      reference_vehicle_tracking_number:
        refVehicleTrackingData?.vehicle_tracking_number,
      direction: '2',

      ...getTransporterAndVehiclePayload(),

      plant_id: vehicleTrackigDetailFormData?.plant,
      unit_of_measurement_id: vehicleTrackigDetailFormData?.unitOfMeasurementId,
      capacity: getNumberOrNull(vehicleTrackigDetailFormData?.capacity),
      is_load_sequence_enabled:
        vehicleTrackigDetailFormData?.enableLoadSequence ?? false,
      driver_name: vehicleTrackigDetailFormData?.driverName,
      driver_mobile_number: vehicleTrackigDetailFormData?.driverMobileNumber,
      driving_license_number: vehicleTrackigDetailFormData?.driverLicenseNumber,
      driver_license_validity: vehicleTrackigDetailFormData?.licenseValidity,
      safety_helmet: vehicleTrackigDetailFormData?.safetyHelmet ?? false,
      safety_shoes: vehicleTrackigDetailFormData?.safetyShoes ?? false,
      safety_handgloves:
        vehicleTrackigDetailFormData?.safetyHandgloves ?? false,
      safety_goggles: vehicleTrackigDetailFormData?.safetyGoggles ?? false,
      trem_card: vehicleTrackigDetailFormData?.tremCard ?? false,
      reflectors: vehicleTrackigDetailFormData?.reflectors ?? false,
      container_number: vehicleTrackigDetailFormData?.containerNumber || null,
      tank_condition: vehicleTrackigDetailFormData?.tankCondition,
      information_panel: vehicleTrackigDetailFormData?.informationPanel,
      tyre_condition: vehicleTrackigDetailFormData?.tyreCondition,
      lid_condition: vehicleTrackigDetailFormData?.lidCondition,
      puc_number: vehicleTrackigDetailFormData?.pucNumber,
      puc_validity_end_date: vehicleTrackigDetailFormData?.pucEndDate,
      puc_validity_start_date: vehicleTrackigDetailFormData?.pucStartDate,
      registration_certificate:
        vehicleTrackigDetailFormData?.registrationCertificate,
      insurance_number: vehicleTrackigDetailFormData?.insuranceNumber,
      insurance_start_date: vehicleTrackigDetailFormData?.insuranceStartDate,
      insurance_end_date: vehicleTrackigDetailFormData?.insuranceEndDate,
      fitness_certificate_number:
        vehicleTrackigDetailFormData?.fitnessCertificateNumber,
      fitness_certificate_start_date:
        vehicleTrackigDetailFormData?.fitnessCertificateStartDate,
      fitness_certificate_end_date:
        vehicleTrackigDetailFormData?.fitnessCertificateEndDate,
      test_report: vehicleTrackigDetailFormData?.coa || null,
      weight_slip: vehicleTrackigDetailFormData?.weightSlip || null,
      remarks: vehicleTrackigDetailFormData?.remarks,
      tare_weight: getNumberOrNull(vehicleTrackigDetailFormData?.tareWeight),
      gross_weight: getNumberOrNull(vehicleTrackigDetailFormData?.grossWeight),
      net_weight: getNumberOrNull(vehicleTrackigDetailFormData?.netWeight),
      is_delivery_attached: isDeliveryAttached,
      delivery_id: isDeliveryAttached ? deliveryIds : [],
      load_headers: loadHeaders,
      is_planned: false,
    };
  }

  const triggerCreateDuplicateApi = useCallback(() => {
    const payload = buildDuplicateTrackingPayload();

    dispatch(createDuplicateVehicleTracking(payload))
      .then((data) => {
        const { vehicleTrackingNumber, workflowStageId } = data || {};
        if (vehicleTrackingNumber && workflowStageId) {
          navigate(`/${workflowStageId}/${vehicleTrackingNumber}`);
        } else {
          navigate('/stations/');
        }
      })
      .finally(() => {
        submitLockRef.current = false;
        setDisableSubmitButton(false);
      });
  }, [
    vehicleTrackigDetailFormData,
    organisationId,
    selectedWorkflowObj,
    selectedStageObj,
    refVehicleTrackingData,
    deliveryLineItem,
    loadLineItem,
    dispatch,
    navigate,
  ]);

  const handleDuplicateTrackingSubmit = useCallback(() => {
    if (submitLockRef.current) {
      return;
    }

    const isMobileNumberValid =
      !vehicleTrackigDetailFormData?.driverMobileNumber ||
      vehicleTrackigDetailFormData.driverMobileNumber.length === 10;

    if (!isMobileNumberValid) {
      setMobileNumberError(true);
      return;
    }

    submitLockRef.current = true;
    setDisableSubmitButton(true);

    // Validate for duplicates before creating
    dispatch(
      validateAndGetDuplicateObjectsForVehicleTracking({
        tenant_organisation_id: organisationId,
        vehicle_number: vehicleTrackigDetailFormData?.vehicleNumber,
        workflow_id: selectedWorkflowObj?.id,
      }),
    )
      .then((response) => {
        if (response?.vehicleTrackingDataValidity) {
          // No duplicate found — proceed to create
          triggerCreateDuplicateApi();
        } else {
          // Duplicate found — show modal
          submitLockRef.current = false;
          setDisableSubmitButton(false);
          setIsDuplicacyModalOpen(true);
        }
      })
      .catch(() => {
        // If validation API fails, fall through to allow creation
        submitLockRef.current = false;
        setDisableSubmitButton(false);
      });
  }, [
    vehicleTrackigDetailFormData,
    organisationId,
    dispatch,
    triggerCreateDuplicateApi,
  ]);

  function handleDuplicacyModalBack() {
    setIsDuplicacyModalOpen(false);
  }

  function handleDuplicacyOverrideProceed() {
    if (submitLockRef.current) return;
    submitLockRef.current = true;
    setIsDuplicacyModalOpen(false);
    setDisableSubmitButton(true);
    triggerCreateDuplicateApi();
  }

  useEffect(() => {
    dispatch(
      getVehicleTrackingDetail(
        refVehicleTrackingData?.vehicle_tracking_number,
        organisationId,
        refVehicleTrackingData?.workflow_stage?.id,
      ),
    );
  }, [dispatch, refVehicleTrackingData?.vehicle_tracking_number]);

  let vehicleOption = null;

  if (vehicleTrackingDetails?.vehicle) {
    vehicleOption = {
      value: vehicleTrackingDetails.vehicle.id,
      label: vehicleTrackingDetails.vehicle.name,
    };
  } else if (vehicleTrackingDetails?.vehicleNumber) {
    vehicleOption = {
      value: vehicleTrackingDetails.vehicleNumber,
      label: vehicleTrackingDetails.vehicleNumber,
    };
  }

  const transporterId = vehicleTrackingDetails?.transporter
    ? vehicleTrackingDetails.transporter.id
    : vehicleTrackingDetails?.transporterExtraData ?? null;

  const transporterName = vehicleTrackingDetails?.transporter
    ? vehicleTrackingDetails.transporter.name
    : vehicleTrackingDetails?.transporterExtraData ?? null;

  // Prefill
  useEffect(() => {
    if (prefillFormDataFromRef.current) return;
    if (!vehicleTrackingDetails?.vehicleTrackingNumber) return;

    setVehicleTrackigDetailFormData((prev) => ({
      ...prev,
      transporterId: transporterId ?? null,
      transporterName: transporterName ?? null,
      selectedVehicleOption: vehicleOption,
      vehicleNumber: vehicleOption?.label ?? null,
      workflowId: vehicleTrackingDetails.workflow?.id ?? null,
      plant: vehicleTrackingDetails.plant?.id ?? null,
      driverName: vehicleTrackingDetails.driverName ?? null,
      driverMobileNumber: vehicleTrackingDetails.driverMobileNumber ?? null,
      driverLicenseNumber: vehicleTrackingDetails.drivingLicenseNumber ?? null,
      licenseValidity: vehicleTrackingDetails.driverLicenseValidity ?? null,
      remarks: vehicleTrackingDetails.remarks ?? null,
      pucNumber: vehicleTrackingDetails.pucNumber ?? null,
      pucStartDate: vehicleTrackingDetails.pucValidityStartDate ?? null,
      pucEndDate: vehicleTrackingDetails.pucValidUpto ?? null,
      registrationCertificate:
        vehicleTrackingDetails.registrationCertificate ?? null,
      insuranceNumber: vehicleTrackingDetails.insuranceNumber ?? null,
      insuranceStartDate: vehicleTrackingDetails.insuranceStartDate ?? null,
      insuranceEndDate: vehicleTrackingDetails.insuranceEndDate ?? null,
      fitnessCertificateNumber:
        vehicleTrackingDetails.fitnessCertificateNumber ?? null,
      fitnessCertificateStartDate:
        vehicleTrackingDetails.fitnessCertificateStartDate ?? null,
      fitnessCertificateEndDate:
        vehicleTrackingDetails.fitnessCertificateEndDate ?? null,
      capacity: vehicleTrackingDetails.capacity ?? null,
      unitOfMeasurementId:
        vehicleTrackingDetails.capacityUnitOfMeasurement?.id ?? null,
      tankCondition: vehicleTrackingDetails.tankCondition?.id ?? null,
      informationPanel: vehicleTrackingDetails.informationPanel?.id ?? null,
      lidCondition: vehicleTrackingDetails.lidCondition?.id ?? null,
      tyreCondition: vehicleTrackingDetails.tyreCondition?.id ?? null,
      tremCard: vehicleTrackingDetails.tremCard ?? false,
      reflectors: vehicleTrackingDetails.reflectors ?? false,
      safetyHelmet: vehicleTrackingDetails.safetyHelmet ?? false,
      safetyShoes: vehicleTrackingDetails.safetyShoes ?? false,
      safetyHandgloves: vehicleTrackingDetails.safetyHandgloves ?? false,
      safetyGoggles: vehicleTrackingDetails.safetyGoggles ?? false,
    }));

    if (vehicleTrackingDetails.workflow?.id) {
      setSelectedWorkflowObj({
        id: vehicleTrackingDetails.workflow.id,
        name: vehicleTrackingDetails.workflow.name,
      });
    }

    if (vehicleTrackingDetails.workflowStage?.id) {
      setSelectedStageObj({
        id: vehicleTrackingDetails.workflowStage.id,
        name: vehicleTrackingDetails.workflowStage.name,
      });
    }

    prefillFormDataFromRef.current = true;
  }, [vehicleTrackingDetails]);

  useEffect(() => {
    const activeDeliveryIds = new Set(
      (deliveryLineItem || []).map(
        (deliveryObj) => deliveryObj.delivery_unique_number,
      ),
    );

    setLoadLineItem((prevLoads) => {
      const cleanedLoads = (prevLoads || [])
        .map((loadObj) => ({
          ...loadObj,
          deliveries: (loadObj.deliveries || []).filter((deliveryObj) =>
            activeDeliveryIds.has(deliveryObj.delivery_unique_number),
          ),
        }))
        .filter((loadObj) => loadObj.deliveries.length > 0);

      return updateLoadSequence(cleanedLoads);
    });
  }, [deliveryLineItem]);

  const totalSteps = duplicateVehicleTrackingSteps.length;

  useEffect(
    () => () => {
      dispatch(resetVehicleTrackingDetails());

      setVehicleTrackigDetailFormData({
        transporterId: null,
        transporterName: null,
        selectedVehicleOption: null,
        vehicleNumber: null,
        workflowId: null,
        plant: null,
        driverName: null,
        driverMobileNumber: null,
        driverLicenseNumber: null,
        licenseValidity: null,
        remarks: null,
        pucNumber: null,
        pucStartDate: null,
        pucEndDate: null,
        registrationCertificate: null,
        insuranceNumber: null,
        insuranceStartDate: null,
        insuranceEndDate: null,
        fitnessCertificateNumber: null,
        fitnessCertificateStartDate: null,
        fitnessCertificateEndDate: null,
        capacity: null,
        unitOfMeasurementId: null,
        tankCondition: null,
        informationPanel: null,
        lidCondition: null,
        tyreCondition: null,
        tremCard: false,
        reflectors: false,
        safetyHelmet: false,
        safetyShoes: false,
        safetyHandgloves: false,
        safetyGoggles: false,
      });

      setMobileNumberError(false);
      setSelectedWorkflowObj(null);
      setSelectedStageObj(null);
      setDeliveryLineItem([]);
      setLoadLineItem([]);
      setDisableSubmitButton(false);
      setIsDuplicacyModalOpen(false);

      prefillFormDataFromRef.current = false;
    },
    [dispatch],
  );

  return (
    <div className="div-flex-column page-layout">
      {/* Duplicacy Modal */}
      <Modal
        show={isDuplicacyModalOpen}
        onHide={() => handleDuplicacyModalBack()}
        centered
        backdrop="static"
        keyboard={false}
        size="lg"
        dialogClassName="vt-duplicacy-modal-dialog"
      >
        <Modal.Body className="p-0">
          <VehicleTrackingDuplicacyModal
            duplicateData={
              vehicleTrackingOutwardReducer.validateAndGetDuplicateObjectsForVehicleTracking
            }
            onBack={() => handleDuplicacyModalBack()}
            onProceed={() => handleDuplicacyOverrideProceed()}
            isProceeding={disableSubmitButton}
            themeColor={themeColor}
            attemptedVehicleNumber={vehicleTrackigDetailFormData?.vehicleNumber}
          />
        </Modal.Body>
      </Modal>

      <div className="form-header-container div-flex-column">
        <div className="div-flex-column rg-5">
          <div className="div-flex-row left-padding cg-5">
            <Breadcrumb />
          </div>
        </div>
        <div className="form-header-container div-flex-row div-space-between border-bottom div-align-center w-100 right-padding">
          <div className="div-space-between div-align-center div-flex-row cg-10 slider-padding px-3">
            <span className="text-xxl text-bold">
              Duplicate Vehicle Tracking
            </span>
            <span className="text-xs">
              Ref: {refVehicleTrackingData.vehicle_tracking_code}
            </span>
          </div>
        </div>
      </div>

      <div className=" div-flex-row duplicate-vehicle-tracking-content cg-10">
        <Col
          md={3}
          lg={3}
          sm={12}
          className={`duplicate-vehicle-tracking-sidebar theme-background-${themeColor}`}
        >
          <div className="duplicate-vehicle-tracking-step-list">
            {duplicateVehicleTrackingSteps.map((step) => {
              const isComplete = step.id <= selectedStep;
              const isClickable =
                step.id <= selectedStep || step.id === selectedStep + 1;

              return (
                <button
                  type="button"
                  key={step.id}
                  className={`duplicate-vehicle-tracking-step ${
                    isComplete ? 'is-complete' : ''
                  } ${isClickable ? '' : 'is-disabled'}`}
                  onClick={() => handleStepClick(step.id)}
                  disabled={!isClickable}
                  aria-disabled={!isClickable}
                >
                  <span className="duplicate-vehicle-tracking-step-number">
                    {String(step.id).padStart(2, '0')}
                  </span>
                  <span className="duplicate-vehicle-tracking-step-text">
                    <span className="duplicate-vehicle-tracking-step-title">
                      {step.title}
                    </span>
                    {step.description ? (
                      <span className="duplicate-vehicle-tracking-step-description">
                        {step.description}
                      </span>
                    ) : null}
                  </span>
                </button>
              );
            })}
          </div>
        </Col>

        <Col md={9} lg={9} sm={12} className="duplicate-vehicle-tracking-panel">
          <div
            className="duplicate-vehicle-tracking-panel-animated "
            key={selectedStep}
          >
            {selectedStep === 1 && (
              <VehicleTrackingDetails
                formData={vehicleTrackigDetailFormData}
                setFormData={setVehicleTrackigDetailFormData}
                handleOnChange={(e) => handleVehicleTrackingDetailChange(e)}
                errorField={false}
                mobileNumberError={mobileNumberError}
                selectedStep={selectedStep}
                setSelectedStep={setSelectedStep}
                totalSteps={totalSteps}
              />
            )}
            {selectedStep === 2 && (
              <UpdatePlanning
                deliveryLineItem={deliveryLineItem}
                setDeliveryLineItem={setDeliveryLineItem}
                loadLineItem={loadLineItem}
                setLoadLineItem={setLoadLineItem}
                organisationId={organisationId}
                vehicleTrackingNumber={
                  refVehicleTrackingData?.vehicle_tracking_number
                }
                formData={vehicleTrackigDetailFormData}
                setFormData={setVehicleTrackigDetailFormData}
                handleOnChange={(e) => handleVehicleTrackingDetailChange(e)}
                errorField={false}
                mobileNumberError={mobileNumberError}
                isEditMode
                selectedStep={selectedStep}
                setSelectedStep={setSelectedStep}
                totalSteps={totalSteps}
              />
            )}
            {selectedStep === 3 && (
              <SelectWorkflow
                selectedWorkflowObj={selectedWorkflowObj}
                setSelectedWorkflowObj={setSelectedWorkflowObj}
                selectedStageObj={selectedStageObj}
                setSelectedStageObj={setSelectedStageObj}
                formData={vehicleTrackigDetailFormData}
                setFormData={setVehicleTrackigDetailFormData}
                handleOnChange={(e) => handleVehicleTrackingDetailChange(e)}
                errorField={false}
                mobileNumberError={mobileNumberError}
                isEditMode
                selectedStep={selectedStep}
                setSelectedStep={setSelectedStep}
                totalSteps={totalSteps}
              />
            )}
            {selectedStep === 4 && (
              <ReviewAndConfirm
                formData={vehicleTrackigDetailFormData}
                setFormData={setVehicleTrackigDetailFormData}
                handleOnChange={(e) => handleVehicleTrackingDetailChange(e)}
                errorField={false}
                mobileNumberError={mobileNumberError}
                deliveryLineItem={deliveryLineItem}
                loadLineItem={loadLineItem}
                selectedWorkflowObj={selectedWorkflowObj}
                selectedStageObj={selectedStageObj}
                isEditMode
                selectedStep={selectedStep}
                setSelectedStep={setSelectedStep}
                totalSteps={totalSteps}
                disableSubmitButton={disableSubmitButton}
                handleDuplicateTrackingSubmit={handleDuplicateTrackingSubmit}
              />
            )}
          </div>
        </Col>
      </div>
    </div>
  );
}
